import { ImmediateActionEnum, NotifiedPartyEnum } from '@lmc/validations'
import { Form as AntdForm, Checkbox, type FormInstance } from 'antd'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import IncidentCaseV2StepForm from '../IncidentCaseV2StepForm'
import type {
  IncidentCaseV2StepProps,
  IncidentCaseV2StepValues
} from '../incidentCaseV2Steps'
import StepCheckbox from '../StepCheckbox'
import StepField, { useStepFieldLabelId } from '../StepField'
import { StepSection, stepChoiceColumn } from '../stepLayout'
import {
  hasNotifiedPartyDetail,
  type NotifiedPartyValue,
  savedNotifiedParties
} from '../stepValues'

const testId = 'incident-case-v2-immediate-actions'

/** The four checklist items stored as notified parties, shared with Notifications. */
const OWNED_PARTIES = [
  NotifiedPartyEnum.gp,
  NotifiedPartyEnum.districtNurse,
  NotifiedPartyEnum.familyNextOfKin,
  NotifiedPartyEnum.manager
] as const

type OwnedParty = (typeof OWNED_PARTIES)[number]
type ChecklistItem = ImmediateActionEnum | OwnedParty

/** The checklist in design order, mixing actions and notified parties. */
export const CHECKLIST: ReadonlyArray<{
  value: ChecklistItem
  labelKey: string
}> = [
  {
    value: ImmediateActionEnum.firstAidGiven,
    labelKey: incidentReportsConstants.CASE_V2_IMMEDIATE_FIRST_AID_GIVEN
  },
  {
    value: ImmediateActionEnum.ambulanceCalled,
    labelKey: incidentReportsConstants.CASE_V2_IMMEDIATE_AMBULANCE_CALLED
  },
  {
    value: NotifiedPartyEnum.gp,
    labelKey: incidentReportsConstants.CASE_V2_IMMEDIATE_CONTACTED_GP
  },
  {
    value: NotifiedPartyEnum.districtNurse,
    labelKey:
      incidentReportsConstants.CASE_V2_IMMEDIATE_CONTACTED_DISTRICT_NURSE
  },
  {
    value: ImmediateActionEnum.accidentIncidentRecordMade,
    labelKey:
      incidentReportsConstants.CASE_V2_IMMEDIATE_ACCIDENT_INCIDENT_RECORD_MADE
  },
  {
    value: NotifiedPartyEnum.familyNextOfKin,
    labelKey: incidentReportsConstants.CASE_V2_IMMEDIATE_CONTACTED_FAMILY
  },
  {
    value: NotifiedPartyEnum.manager,
    labelKey: incidentReportsConstants.CASE_V2_IMMEDIATE_MANAGER_NOTIFIED
  },
  {
    value: ImmediateActionEnum.carePlanCreated,
    labelKey: incidentReportsConstants.CASE_V2_IMMEDIATE_CARE_PLAN_CREATED
  },
  {
    value: ImmediateActionEnum.carePlanReviewed,
    labelKey: incidentReportsConstants.CASE_V2_IMMEDIATE_CARE_PLAN_REVIEWED
  },
  {
    value: ImmediateActionEnum.riskAssessmentCreated,
    labelKey: incidentReportsConstants.CASE_V2_IMMEDIATE_RISK_ASSESSMENT_CREATED
  },
  {
    value: ImmediateActionEnum.riskAssessmentReviewed,
    labelKey:
      incidentReportsConstants.CASE_V2_IMMEDIATE_RISK_ASSESSMENT_REVIEWED
  }
]

const isOwnedParty = (item: string): item is OwnedParty =>
  (OWNED_PARTIES as ReadonlyArray<string>).includes(item)

type ImmediateActionsValues = {
  checked: Array<ChecklistItem>
  /** Parties unticked with their saved detail discarded; ticked again, they start empty. */
  discarded: Array<OwnedParty>
}

const discardedIn = (form: FormInstance): Array<OwnedParty> =>
  form.getFieldValue('discarded') ?? []

/** The case's immediate actions; ts-common's case type lacks them. */
export type CaseImmediateActions = {
  immediateActions?: IncidentCaseV2StepValues['immediateActions'] | null
}

/**
 * Step 2: one checklist of what was done at the time. Seven items are immediate
 * actions; contacting the GP, district nurse or family and notifying a manager
 * are notified parties, a list the Notifications step also writes. A save
 * replaces that list whole, so this step sends all of it, keeping the other
 * step's entries and any detail already recorded against a ticked party.
 */
const ImmediateActionsStep = ({
  incidentReport,
  handleRef,
  onDirtyChange
}: IncidentCaseV2StepProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  const savedParties = useMemo(
    () => savedNotifiedParties(incidentReport),
    [incidentReport]
  )

  const initialValues = useMemo<ImmediateActionsValues>(() => {
    const savedActions =
      (incidentReport.case as CaseImmediateActions | null)?.immediateActions ??
      []
    const ticked = new Set<ChecklistItem>([
      ...savedActions,
      ...savedParties.map((entry) => entry.party).filter(isOwnedParty)
    ])

    return {
      checked: CHECKLIST.map(({ value }) => value).filter((value) =>
        ticked.has(value)
      ),
      discarded: []
    }
  }, [incidentReport, savedParties])

  const savedEntries = (party: OwnedParty) =>
    savedParties.filter((entry) => entry.party === party)

  const toStepValues = ({
    checked,
    discarded
  }: ImmediateActionsValues): IncidentCaseV2StepValues => {
    const isChecked = (item: ChecklistItem) => checked.includes(item)
    const nextParties = OWNED_PARTIES.filter(isChecked).flatMap(
      (party): Array<NotifiedPartyValue> => {
        const saved = savedEntries(party)
        return saved.length > 0 && !discarded.includes(party)
          ? saved
          : [{ party }]
      }
    )

    return {
      immediateActions: CHECKLIST.map(({ value }) => value).filter(
        (value): value is ImmediateActionEnum =>
          !isOwnedParty(value) && isChecked(value)
      ),
      // Only its own parties: the server keeps the ones this step doesn't own.
      notifiedParties: nextParties
    }
  }

  return (
    <IncidentCaseV2StepForm
      handleRef={handleRef}
      onDirtyChange={onDirtyChange}
      initialValues={initialValues}
      toStepValues={toStepValues}
      testId={testId}
    >
      <StepSection>
        <StepField
          name="checked"
          label={t(incidentReportsConstants.CASE_V2_IMMEDIATE_ACTIONS_TAKEN)}
          hint={t(
            incidentReportsConstants.CASE_V2_IMMEDIATE_ACTIONS_TAKEN_HINT
          )}
        >
          <Checklist
            hasSavedDetail={(party, form) =>
              !discardedIn(form).includes(party) &&
              savedEntries(party).some(hasNotifiedPartyDetail)
            }
          />
        </StepField>
      </StepSection>
    </IncidentCaseV2StepForm>
  )
}

type ChecklistProps = {
  /** Whether a party has saved detail that unticking it would drop. */
  hasSavedDetail: (party: OwnedParty, form: FormInstance) => boolean
  /** Injected by the form item. */
  id?: string
  onChange?: (checked: Array<ChecklistItem>) => void
  value?: Array<ChecklistItem>
}

/**
 * The checklist's ticks. A party's tick asks before dropping detail recorded
 * against it on the Notifications step, as it does there.
 */
const Checklist = ({
  hasSavedDetail,
  id,
  onChange,
  value = []
}: ChecklistProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const form = AntdForm.useFormInstance()
  const labelId = useStepFieldLabelId()

  const toggle = (item: ChecklistItem, isChecked: boolean) =>
    onChange?.(
      CHECKLIST.map((entry) => entry.value).filter((entry) =>
        entry === item ? isChecked : value.includes(entry)
      )
    )

  return (
    <Ticks id={id} role="group" aria-labelledby={labelId}>
      {CHECKLIST.map(({ value: item, labelKey }) =>
        isOwnedParty(item) ? (
          <StepCheckbox
            key={item}
            checked={value.includes(item)}
            confirmUntick={{
              body: t(
                incidentReportsConstants.CASE_V2_NOTIFICATIONS_DISCARD_BODY
              ),
              isNeeded: () => hasSavedDetail(item, form),
              onConfirm: () =>
                form.setFieldValue('discarded', [...discardedIn(form), item]),
              title: t(
                incidentReportsConstants.CASE_V2_NOTIFICATIONS_DISCARD_TITLE
              )
            }}
            onChange={(isChecked) => toggle(item, isChecked)}
            testId={`${testId}-${item}`}
          >
            {t(labelKey)}
          </StepCheckbox>
        ) : (
          <Checkbox
            key={item}
            checked={value.includes(item)}
            data-testid={`${testId}-${item}`}
            onChange={(event) => toggle(item, event.target.checked)}
          >
            {t(labelKey)}
          </Checkbox>
        )
      )}
    </Ticks>
  )
}

const Ticks = styled.div`
  ${stepChoiceColumn}
`

export default ImmediateActionsStep
